import type { Permission } from '@zenarty/shared';
import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router';
import { useAuth } from './auth';
import { AppShell } from './layout/AppShell';
import { ChangePasswordPage } from './pages/Account';
import { AuditPage } from './pages/Audit';
import { BarcodePage } from './pages/Barcode';
import { ConfigurationPage } from './pages/Configuration';
import { ItemsPage } from './pages/Items';
import { InventoryPage } from './pages/Inventory';
import { CountsPage, HoldsPage, TracePage } from './pages/InventoryOps';
import { ReceiptsPage } from './pages/Inbound';
import {
  PackingPage,
  PickingPage,
  PickListPage,
  ReplenishmentPage,
  WavesPage,
} from './pages/Fulfillment';
import { OrderDetailPage } from './pages/OrderDetail';
import { OrderBoardPage, OrdersPage } from './pages/Orders';
import { LocationsPage } from './pages/Locations';
import { PutawayPage, PutawayRulesPage } from './pages/Putaway';
import { ReceiptDetailPage } from './pages/ReceiptDetail';
import { PartnersPage } from './pages/Partners';
import { ClientsPage } from './pages/Clients';
import { DashboardPage } from './pages/Dashboard';
import { DevicesPage } from './pages/Devices';
import { LoginPage } from './pages/Login';
import { SitesPage } from './pages/Sites';
import { UsersPage } from './pages/Users';

function Root() {
  const { status, me } = useAuth();
  if (status === 'loading')
    return (
      <div className="splash">
        <div className="spinner" />
      </div>
    );
  if (status === 'anonymous' || !me) return <LoginPage />;
  if (me.passwordChangeRequired)
    return (
      <AppShell crumb="Change password">
        <ChangePasswordPage />
      </AppShell>
    );
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

function Guard({ permission, children }: { permission: Permission; children: ReactNode }) {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <Navigate to="/" replace />;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    children: [
      { index: true, element: <DashboardPage /> },
      {
        path: 'sites',
        element: (
          <Guard permission="site:read">
            <SitesPage />
          </Guard>
        ),
      },
      {
        path: 'clients',
        element: (
          <Guard permission="client:read">
            <ClientsPage />
          </Guard>
        ),
      },
      {
        path: 'users',
        element: (
          <Guard permission="user:read">
            <UsersPage />
          </Guard>
        ),
      },
      {
        path: 'devices',
        element: (
          <Guard permission="device:read">
            <DevicesPage />
          </Guard>
        ),
      },
      {
        path: 'audit',
        element: (
          <Guard permission="audit:read">
            <AuditPage />
          </Guard>
        ),
      },
      {
        path: 'items',
        element: (
          <Guard permission="item:read">
            <ItemsPage />
          </Guard>
        ),
      },
      {
        path: 'locations',
        element: (
          <Guard permission="location:read">
            <LocationsPage />
          </Guard>
        ),
      },
      {
        path: 'partners',
        element: (
          <Guard permission="partner:read">
            <PartnersPage />
          </Guard>
        ),
      },
      {
        path: 'configuration',
        element: (
          <Guard permission="config:read">
            <ConfigurationPage />
          </Guard>
        ),
      },
      { path: 'tools/barcode', element: <BarcodePage /> },
      {
        path: 'inventory',
        element: (
          <Guard permission="inventory:read">
            <InventoryPage />
          </Guard>
        ),
      },
      {
        path: 'inventory/counts',
        element: (
          <Guard permission="inventory:count">
            <CountsPage />
          </Guard>
        ),
      },
      {
        path: 'inventory/holds',
        element: (
          <Guard permission="inventory:hold">
            <HoldsPage />
          </Guard>
        ),
      },
      {
        path: 'inventory/trace',
        element: (
          <Guard permission="inventory:read">
            <TracePage />
          </Guard>
        ),
      },
      {
        path: 'inbound/receipts',
        element: (
          <Guard permission="inbound:read">
            <ReceiptsPage />
          </Guard>
        ),
      },
      {
        path: 'inbound/receipts/:id',
        element: (
          <Guard permission="inbound:read">
            <ReceiptDetailPage />
          </Guard>
        ),
      },
      {
        path: 'inbound/putaway',
        element: (
          <Guard permission="inbound:read">
            <PutawayPage />
          </Guard>
        ),
      },
      {
        path: 'putaway-rules',
        element: (
          <Guard permission="config:read">
            <PutawayRulesPage />
          </Guard>
        ),
      },
      {
        path: 'outbound/board',
        element: (
          <Guard permission="outbound:read">
            <OrderBoardPage />
          </Guard>
        ),
      },
      {
        path: 'outbound/orders',
        element: (
          <Guard permission="outbound:read">
            <OrdersPage />
          </Guard>
        ),
      },
      {
        path: 'outbound/orders/:id',
        element: (
          <Guard permission="outbound:read">
            <OrderDetailPage />
          </Guard>
        ),
      },
      {
        path: 'outbound/waves',
        element: (
          <Guard permission="outbound:read">
            <WavesPage />
          </Guard>
        ),
      },
      {
        path: 'outbound/picking',
        element: (
          <Guard permission="outbound:read">
            <PickingPage />
          </Guard>
        ),
      },
      {
        path: 'outbound/picking/:id',
        element: (
          <Guard permission="outbound:read">
            <PickListPage />
          </Guard>
        ),
      },
      {
        path: 'outbound/packing',
        element: (
          <Guard permission="outbound:pack">
            <PackingPage />
          </Guard>
        ),
      },
      {
        path: 'outbound/replenishment',
        element: (
          <Guard permission="outbound:read">
            <ReplenishmentPage />
          </Guard>
        ),
      },
      { path: 'account/password', element: <ChangePasswordPage /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
